import { media, type MediaSettings } from '../../webrtc/media';
import { getLogger } from '../../libs/logger';
import {
  MediaSetupStep,
  mediaSetupTimer,
} from '../useCallState/states/callInitTimer';
import { getVirtualBackgroundFilterFromLocalStorage } from '../../components/media/virtualBackground';
import { useIsVirtualBackgroundEnabled } from '../useVirtualBackground';
import { useGetUserId } from '../../hooks/useCredentials';

const logger = getLogger('useLocalMediaActions');

export type UseLocalMediaActionsReturnType = {
  init: () => Promise<void>;
  start: (settings?: MediaSettings) => Promise<void>;
  stop: () => Promise<void>;
  restart: () => Promise<void>;
  toggleVideo: typeof media.toggleVideo;
  toggleAudio: (hasAudio?: boolean) => Promise<void>;
  getImageFromVideo: typeof media.getImageFromVideo;
  flipCamera: typeof media.flipCamera;
  applyVirtualBackground: typeof media.applyVirtualBackground;
};

export function useLocalMediaActions(): UseLocalMediaActionsReturnType {
  const userId = useGetUserId();
  const isVirtualBackgroundEnabled = useIsVirtualBackgroundEnabled();

  const stop = async (): Promise<void> => {
    if (!media.source) {
      return;
    }

    try {
      await media.stopLocalMedia();
    } catch (error) {
      logger.error('[useLocalMediaActions/stop]', error);
    }
  };

  const init = async (): Promise<void> => {
    try {
      await media.init();
    } catch (error) {
      logger.error('[useLocalMediaActions/init]', error);
      throw error;
    }
  };

  const start = async (settings?: MediaSettings): Promise<void> => {
    if (media.hasVideo || media.hasAudio) {
      return;
    }

    try {
      await init();
    } catch (error) {
      logger.error('[useLocalMediaActions/start]', error);
    }

    mediaSetupTimer.step(MediaSetupStep.AccessRequested);

    await media.startLocalMedia({
      ...settings,
      virtualBackgroundFilter: isVirtualBackgroundEnabled
        ? getVirtualBackgroundFilterFromLocalStorage(userId)
        : undefined,
    });

    mediaSetupTimer.step(MediaSetupStep.AccessFulfilled);
  };

  const restart = async (): Promise<void> => {
    return media.restartLocalMedia();
  };

  const toggleAudio = async (hasAudio?: boolean): Promise<void> => {
    if (media.hasAudio === hasAudio) {
      return;
    }

    try {
      await media.toggleAudio(hasAudio);
    } catch {
      // Safe to ignore: error handling is encapsulated within media
    }
  };

  const toggleVideo = async (hasVideo?: boolean): Promise<void> => {
    if (media.hasVideo === hasVideo) {
      return;
    }

    try {
      await media.toggleVideo(hasVideo);
    } catch {
      // Safe to ignore: error handling is encapsulated within media
    }
  };

  return {
    init,
    start,
    stop,
    restart,
    toggleVideo,
    toggleAudio,
    getImageFromVideo: media.getImageFromVideo.bind(media),
    flipCamera: media.flipCamera.bind(media),
    applyVirtualBackground: media.applyVirtualBackground.bind(media),
  };
}
